import { AxiosResponse } from "axios";
import { API } from "../client";
import {
  KakaoSender,
  KakaoTemplate,
  KakaoMessage,
  KakaoSenderCreateRequest,
  KakaoTemplateCreateRequest,
  AlimTalkSendRequest,
  FriendTalkSendRequest,
  SMSSendRequest,
  MessageSendResponse,
  SiteValidationRequest,
  SiteValidationResponse,
  SiteStatusResponse,
  MessageStatusResponse,
  KakaoSenderListResponse,
  KakaoTemplateListResponse,
  KakaoMessageListResponse,
  MessageBulkJob,
  MessageListParams,
  SenderListParams,
  TemplateListParams,
} from "../types/messaging.types";

const V2_MESSAGING_BASE = "/api/v2/messaging";

const cleanParams = <T extends Record<string, unknown>>(params?: T): Partial<T> => {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== "" && value !== undefined && value !== null)
  ) as Partial<T>;
};

/**
 * Messaging API Service
 * Provides methods for Kakao messaging, SMS, and management operations
 */
export class MessagingService {

  // ========================
  // Management API (Super Admin Only)
  // ========================

  /**
   * Create a new Kakao SendProfile via Management API
   */
  static async createSender(data: KakaoSenderCreateRequest): Promise<AxiosResponse<KakaoSender>> {
    return API.post("/messaging/kakao/management/senders/create-profile/", data);
  }

  /**
   * Get all senders (Management API)
   */
  static async getSendersManagement(params?: SenderListParams): Promise<AxiosResponse<KakaoSenderListResponse>> {
    return API.get("/messaging/kakao/management/senders/", { params });
  }

  /**
   * Get sender by ID (Management API)
   */
  static async getSenderManagement(id: number): Promise<AxiosResponse<KakaoSender>> {
    return API.get(`/messaging/kakao/management/senders/${id}/`);
  }

  /**
   * Update sender (Management API)
   */
  static async updateSenderManagement(id: number, data: Partial<KakaoSenderCreateRequest>): Promise<AxiosResponse<KakaoSender>> {
    return API.put(`/messaging/kakao/management/senders/${id}/`, data);
  }

  /**
   * Delete sender (Management API)
   */
  static async deleteSenderManagement(id: number): Promise<AxiosResponse<void>> {
    return API.delete(`/messaging/kakao/management/senders/${id}/`);
  }

  // /**
  //  * Sync sender status with Kakao API - Not exist
  //  */
  // static async syncSenderStatus(id: number): Promise<AxiosResponse<KakaoSender>> {
  //   return API.get(`/messaging/kakao/management/senders/${id}/sync-status/`);
  // }

  /**
   * Create a new Kakao Template via Management API
   */
  static async createTemplate(data: KakaoTemplateCreateRequest): Promise<AxiosResponse<KakaoTemplate>> {
    return API.post("/messaging/kakao/management/senders/create-template/", data);
  }

  /**
   * Get all templates (Management API)
   */
  static async getTemplatesManagement(params?: TemplateListParams): Promise<AxiosResponse<KakaoTemplateListResponse>> {
    return API.get("/messaging/kakao/management/senders/list-templates/", { params });
  }

  /**
   * Get template by ID (Management API)
   */
  static async getTemplateManagement(id: number): Promise<AxiosResponse<KakaoTemplate>> {
    return API.get(`/messaging/kakao/templates/${id}/`);
  }

  /**
   * Update template via Management API
   */
  static async updateTemplateManagement(id: number, data: Partial<KakaoTemplateCreateRequest>): Promise<AxiosResponse<KakaoTemplate>> {
    return API.put(`/messaging/kakao/management/senders/${id}/update-template/`, data);
  }

  /**
   * Delete template (Management API)
   */
  static async deleteTemplateManagement(id: number): Promise<AxiosResponse<void>> {
    return API.delete(`/messaging/kakao/templates/${id}/`);
  }

  /**
   * Sync sender status.
   * Fallbacks to sender detail fetch when dedicated sync endpoint is unavailable.
   */
  static async syncSenderStatus(id: number): Promise<AxiosResponse<KakaoSender>> {
    return API.get(`/messaging/kakao/management/senders/${id}/`);
  }

  // ========================
  // Client API (Staff Users)
  // ========================

  /**
   * Send AlimTalk message
   */
  static async sendAlimTalk(data: AlimTalkSendRequest): Promise<AxiosResponse<MessageSendResponse>> {
    return API.post(`${V2_MESSAGING_BASE}/send/`, {
      channel: "AT",
      ...data,
    });
  }

  /**
   * Send FriendTalk message
   */
  static async sendFriendTalk(data: FriendTalkSendRequest): Promise<AxiosResponse<MessageSendResponse>> {
    return API.post(`${V2_MESSAGING_BASE}/send/`, {
      channel: "BM",
      ...data,
    });
  }

  /**
   * Send SMS/LMS message
   */
  static async sendSMS(data: SMSSendRequest): Promise<AxiosResponse<MessageSendResponse>> {
    return API.post(`${V2_MESSAGING_BASE}/send/`, {
      channel: data.message_type || "SM",
      ...data,
    });
  }

  /**
   * Validate site Kakao configuration
   */
  static async validateSite(data: SiteValidationRequest): Promise<AxiosResponse<SiteValidationResponse>> {
    return API.get(`${V2_MESSAGING_BASE}/provider-health/`, { params: cleanParams(data as unknown as Record<string, unknown>) });
  }

  /**
   * Get Kakao configuration status for all sites
   */
  static async getSiteStatus(): Promise<AxiosResponse<SiteStatusResponse>> {
    return API.get("/messaging/kakao/messages/site-status/");
  }

  /**
   * Check message delivery status
   */
  static async checkMessageStatus(messageId: number): Promise<AxiosResponse<MessageStatusResponse>> {
    return API.get(`/messaging/kakao/messages/${messageId}/check-status/`);
  }

  /**
   * Get all messages
   */
  static async getMessages(params?: MessageListParams): Promise<AxiosResponse<KakaoMessageListResponse>> {
    return API.get(`${V2_MESSAGING_BASE}/history/`, { params: cleanParams(params as unknown as Record<string, unknown>) });
  }

  /**
   * Get message by ID
   */
  static async getMessage(id: number): Promise<AxiosResponse<KakaoMessage>> {
    const response = await API.get<KakaoMessageListResponse>(`${V2_MESSAGING_BASE}/history/`, { params: { id } });
    return {
      ...response,
      data: response.data.results[0],
    } as AxiosResponse<KakaoMessage>;
  }

  /**
   * Upload image for FriendTalk (wide image support)
   */
  static async uploadImage(file: File, siteId?: number, message_type: string = 'friendtalk'): Promise<AxiosResponse<{id:number;url:string;width:number;height:number;size:number;is_wide:boolean;}>> {
    const form = new FormData();
    form.append('image', file);
    if (siteId) form.append('site_id', String(siteId));
    if (message_type) form.append('message_type', message_type);
    return API.post('/messaging/kakao/messages/upload-image/', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Send AlimTalk message in batch
   */
  static async sendAlimTalkBatch(data: import("../types/messaging.types").AlimTalkBatchSendRequest) {
    return API.post<MessageBulkJob>(`${V2_MESSAGING_BASE}/bulk-jobs/`, {
      channel: "AT",
      template_id: data.template_id,
      phone_numbers: data.phone_numbers,
      message: data.message,
    });
  }

  /**
   * Send FriendTalk message in batch
   */
  static async sendFriendTalkBatch(data: import("../types/messaging.types").FriendTalkBatchSendRequest) {
    return API.post<MessageBulkJob>(`${V2_MESSAGING_BASE}/bulk-jobs/`, {
      channel: "BM",
      phone_numbers: data.phone_numbers,
      message: data.message,
    });
  }

  /**
   * Send SMS/LMS message in batch
   */
  static async sendSMSBatch(data: import("../types/messaging.types").SMSBatchSendRequest) {
    return API.post<MessageBulkJob>(`${V2_MESSAGING_BASE}/bulk-jobs/`, {
      channel: data.message_type || "SM",
      phone_numbers: data.phone_numbers,
      message: data.message,
      title: data.title,
    });
  }

  // ========================
  // Read-only Views (Staff Users)
  // ========================

  /**
   * Get all senders (read-only for staff)
   */
  static async getSenders(params?: SenderListParams): Promise<AxiosResponse<KakaoSenderListResponse>> {
    return API.get("/messaging/kakao/senders/", { params });
  }

  /**
   * Get sender by ID (read-only for staff)
   */
  static async getSender(id: number): Promise<AxiosResponse<KakaoSender>> {
    return API.get(`/messaging/kakao/senders/${id}/`);
  }

  /**
   * Get all templates (read-only for staff)
   */
  static async getTemplates(params?: TemplateListParams): Promise<AxiosResponse<KakaoTemplateListResponse>> {
    return API.get("/messaging/kakao/templates/", { params });
  }

  /**
   * Get template by ID (read-only for staff)
   */
  static async getTemplate(id: number): Promise<AxiosResponse<KakaoTemplate>> {
    return API.get(`/messaging/kakao/templates/${id}/`);
  }

  /**
   * Update template (client-facing endpoint)
   * This maps to POST /messaging/kakao/templates/{id}/update/
   */
  static async updateTemplateClient(id: number, data: any): Promise<AxiosResponse<any>> {
    return API.post(`/messaging/kakao/templates/${id}/update/`, data);
  }

  // ========================
  // Convenience Methods
  // ========================

  /**
   * Get active senders for a specific site
   */
  static async getActiveSendersForSite(siteId: number): Promise<AxiosResponse<KakaoSenderListResponse>> {
    return this.getSenders({ 
      site_id: siteId, 
      status: 'active' 
    });
  }

  /**
   * Get approved templates for a specific site
   */
  static async getApprovedTemplatesForSite(siteId: number): Promise<AxiosResponse<KakaoTemplateListResponse>> {
    return this.getTemplates({ 
      site_id: siteId, 
      template_status: 'APR' 
    });
  }

  /**
   * Get templates for a specific sender
   */
  static async getTemplatesForSender(senderId: number): Promise<AxiosResponse<KakaoTemplateListResponse>> {
    return this.getTemplates({ 
      sender_id: senderId,
      template_status: 'APR'
    });
  }

  /**
   * Get recent messages for a site
   */
  static async getRecentMessagesForSite(siteId: number, limit: number = 50): Promise<AxiosResponse<KakaoMessageListResponse>> {
    return this.getMessages({ 
      site_id: siteId, 
      page_size: limit,
      ordering: '-sent_at'
    });
  }

  /**
   * Get message history for a phone number
   */
  static async getMessageHistoryForPhone(phoneNumber: string, siteId?: number): Promise<AxiosResponse<KakaoMessageListResponse>> {
    const params: MessageListParams = { 
      phone_number: phoneNumber,
      ordering: '-sent_at'
    };
    if (siteId) {
      params.site_id = siteId;
    }
    return this.getMessages(params);
  }

  /**
   * Get failed messages for a site
   */
  static async getFailedMessagesForSite(siteId: number): Promise<AxiosResponse<KakaoMessageListResponse>> {
    return this.getMessages({ 
      site_id: siteId,
      // Assuming failed messages have status codes that indicate failure
      // You might need to adjust this based on your actual status codes
      ordering: '-sent_at'
    });
  }

  /**
   * Search templates by content
   */
  static async searchTemplates(searchTerm: string, siteId?: number): Promise<AxiosResponse<KakaoTemplateListResponse>> {
    const params: TemplateListParams = { 
      search: searchTerm,
      template_status: 'APR'
    };
    if (siteId) {
      params.site_id = siteId;
    }
    return this.getTemplates(params);
  }

  /**
   * Get statistics for a site's messaging
   */
  static async getMessagingStats(siteId: number, dateFrom?: string, dateTo?: string): Promise<AxiosResponse<KakaoMessageListResponse>> {
    const params: MessageListParams = { 
      site_id: siteId
    };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    
    return this.getMessages(params);
  }
}

// Export default instance for convenience
export const messagingService = MessagingService;
